import { Button } from '../../components/ui/button';
import { FileText, Building, MapPin, Shield, User, RefreshCw, ExternalLink, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DocumentRow = ({ doc, headers, loadDocs, deleteDoc }) => {
  const isOverdue = doc.next_review_date && new Date(doc.next_review_date) < new Date();

  const post = async (path, okMsg) => {
    const res = await fetch(`${API}/api/appetite/documents/${doc.id}/${path}`, { method: 'POST', headers: headers() });
    if (res.ok) { toast.success(okMsg); }
    else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Action failed'); }
    loadDocs();
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isOverdue ? 'bg-red-50/30 border-red-200' : 'bg-white border-gray-200 hover:border-gray-300'}`}
      data-testid={`doc-${doc.id}`}>
      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
        <FileText size={16} className="text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-bold text-gray-800">{doc.title}</span>
          {doc.indexing_status === 'processing' && <span className="text-[8px] font-bold bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded animate-pulse">INDEXING…</span>}
          {doc.indexing_status === 'failed' && <span className="text-[8px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded" title={doc.indexing_error || 'Indexing failed'}>INDEX FAILED</span>}
          {isOverdue && <span className="text-[8px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded">OVERDUE</span>}
          {doc.status === 'active' && !isOverdue && doc.indexing_status !== 'processing' && doc.indexing_status !== 'failed' && <span className="text-[8px] font-bold bg-green-100 text-green-600 px-1.5 py-0.5 rounded">ACTIVE</span>}
          {doc.reminder_paused && <span className="text-[8px] font-bold bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded">PAUSED</span>}
          {doc.escalated && <span className="text-[8px] font-bold bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">ESCALATED</span>}
          {doc.reminder_count > 0 && <span className="text-[8px] text-gray-400">{doc.reminder_count} reminder(s)</span>}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-[10px] text-blue-600 font-medium flex items-center gap-0.5"><Building size={9} /> {doc.carrier}</span>
          {doc.state && <span className="text-[10px] text-green-600 flex items-center gap-0.5"><MapPin size={9} /> {doc.state}</span>}
          {doc.lob && <span className="text-[10px] text-purple-600 flex items-center gap-0.5"><Shield size={9} /> {doc.lob}</span>}
          {doc.effective_date && <span className="text-[10px] text-gray-400">Eff: {doc.effective_date}</span>}
          {doc.assigned_to_name && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><User size={9} /> {doc.assigned_to_name}</span>}
          <span className="text-[10px] text-gray-400">{(doc.versions || []).length} version(s)</span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {doc.indexing_status === 'failed' && (
          <button onClick={(e) => { e.stopPropagation(); post('reindex', 'Re-indexing started'); }}
            className="text-[10px] text-blue-600 bg-blue-50 border border-blue-200 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1" data-testid={`reindex-${doc.id}`}>
            <RefreshCw size={9} /> Re-index
          </button>
        )}
        {doc.reminder_paused ? (
          <button onClick={(e) => { e.stopPropagation(); post('resume-reminders', 'Reminders resumed'); }}
            className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded hover:bg-amber-100" data-testid={`resume-${doc.id}`}>
            Resume
          </button>
        ) : (
          <button onClick={(e) => { e.stopPropagation(); post('pause-reminders', 'Reminders paused'); }}
            className="text-[10px] text-gray-400 hover:text-amber-600 px-2 py-1 rounded hover:bg-amber-50" title="Pause reminders" data-testid={`pause-${doc.id}`}>
            Pause
          </button>
        )}
        {isOverdue && (
          <button onClick={(e) => { e.stopPropagation(); post('send-reminder', 'Reminder sent'); }}
            className="text-[10px] text-blue-600 bg-blue-50 border border-blue-200 px-2 py-1 rounded hover:bg-blue-100" data-testid={`send-reminder-${doc.id}`}>
            Send Reminder
          </button>
        )}
        {doc.versions?.[0]?.file_url && (
          <a href={`${API}${doc.versions[doc.versions.length - 1].file_url}`} target="_blank" rel="noreferrer"
            className="text-gray-300 hover:text-blue-500 p-1.5 rounded hover:bg-blue-50"><ExternalLink size={12} /></a>
        )}
        <button onClick={() => deleteDoc(doc.id)} className="text-gray-200 hover:text-red-400 p-1.5 rounded hover:bg-red-50"><Trash2 size={12} /></button>
      </div>
    </div>
  );
};

export const DocumentsTab = ({ documents, filters, docCarrier, setDocCarrier, docState, setDocState, headers, loadDocs, deleteDoc, onUploadClick }) => (
  <div className="flex-1 overflow-y-auto p-6">
    <div className="flex items-center gap-2 mb-4 flex-wrap">
      <select value={docCarrier} onChange={e => setDocCarrier(e.target.value)} className="h-8 text-xs border rounded-md px-2 bg-white">
        <option value="">All Carriers</option>
        {filters.carriers.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={docState} onChange={e => setDocState(e.target.value)} className="h-8 text-xs border rounded-md px-2 bg-white">
        <option value="">All States</option>
        {filters.states.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <span className="text-xs text-gray-400 ml-2">{documents.length} documents</span>
    </div>

    {documents.length === 0 && (
      <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
        <FileText size={36} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-400 text-sm mb-1">No underwriting guidelines uploaded yet</p>
        <p className="text-gray-300 text-xs mb-4">Upload carrier appetite guides to start searching</p>
        <Button size="sm" onClick={onUploadClick} className="bg-[#101C47] text-xs gap-1"><Upload size={12} /> Upload First Document</Button>
      </div>
    )}

    <div className="space-y-2">
      {documents.map(doc => (
        <DocumentRow key={doc.id} doc={doc} headers={headers} loadDocs={loadDocs} deleteDoc={deleteDoc} />
      ))}
    </div>
  </div>
);
